import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Eye, EyeOff, Save, Globe, History, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SECTION_LABELS = {
  hero: "Carrossel de banners (topo)",
  categories: "Categorias ilustradas",
  offers: "Vitrine de ofertas",
  promo_banner: "Banner promocional",
  bestsellers: "Mais vendidos (vendas reais)",
  featured: "Produtos em destaque",
  trust: "Como comprar (confiança)",
  reviews: "Avaliações de clientes",
  catalog: "Catálogo completo (chamada para /catalogo)",
};

// Editor visual da página inicial: reordenar, ocultar, renomear seções.
// Rascunho → Publicar (com histórico para restaurar).
export default function AdminHomeEditor() {
  const [sections, setSections] = useState(null);
  const [published, setPublished] = useState([]);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const load = () => {
    api.get("/admin/settings").then((r) => {
      const live = r.data.home_sections || [];
      const draft = r.data.home_sections_draft || live;
      setPublished(live);
      setSections(draft.map((s) => ({ ...s })));
      setHistory(r.data.home_sections_history || []);
    });
  };
  useEffect(load, []);

  const move = (i, dir) => {
    const next = [...sections];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setSections(next);
  };

  const isDirty = JSON.stringify(sections) !== JSON.stringify(published);

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.put("/admin/home-sections/draft", { sections });
      toast.success("Rascunho salvo! A loja continua com a versão publicada.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await api.put("/admin/home-sections/draft", { sections });
      await api.post("/admin/home-sections/publish");
      toast.success("Home publicada! Confira a página inicial.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPublishing(false);
    }
  };

  const restore = async (index) => {
    try {
      await api.post(`/admin/home-sections/restore/${index}`);
      toast.success("Versão anterior restaurada e publicada.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!sections) return <div className="p-8"><div className="animate-pulse h-40 bg-white rounded-2xl" /></div>;

  return (
    <div className="p-4 sm:p-8 max-w-3xl" data-testid="admin-home-editor">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
        <LayoutTemplate className="w-6 h-6 text-brand" /> Editor da Página Inicial
      </h1>
      <p className="text-sm text-gray-500 mt-1 mb-6">
        Reordene, oculte e renomeie as seções da home. Salve como rascunho e publique quando estiver pronto —
        a versão anterior fica guardada para restauração.
      </p>

      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="home-sections-list">
        {sections.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2 p-3.5" data-testid={`home-section-${s.id}`}>
            <span className="text-xs font-bold text-slate-300 w-6">{i + 1}º</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800">{SECTION_LABELS[s.type] || s.type}</p>
              {["categories", "offers", "bestsellers", "featured", "reviews"].includes(s.type) && (
                <Input
                  value={s.title || ""}
                  onChange={(e) => setSections(sections.map((x) => (x.id === s.id ? { ...x, title: e.target.value } : x)))}
                  placeholder="Título da seção (opcional)"
                  className="mt-1.5 h-8 text-xs"
                  data-testid={`section-title-${s.id}`}
                />
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`section-up-${s.id}`}>
                <ArrowUp className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(i, 1)} disabled={i === sections.length - 1} data-testid={`section-down-${s.id}`}>
                <ArrowDown className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setSections(sections.map((x) => (x.id === s.id ? { ...x, visible: !x.visible } : x)))}
                title={s.visible !== false ? "Ocultar" : "Mostrar"}
                data-testid={`section-toggle-${s.id}`}
              >
                {s.visible !== false ? <Eye className="w-4 h-4 text-brand" /> : <EyeOff className="w-4 h-4 text-slate-300" />}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <Button variant="outline" onClick={saveDraft} disabled={saving} className="rounded-full" data-testid="home-save-draft">
          <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar rascunho"}
        </Button>
        <Button onClick={publish} disabled={publishing || !isDirty} className="rounded-full" data-testid="home-publish">
          <Globe className="w-4 h-4" /> {publishing ? "Publicando..." : "Publicar na loja"}
        </Button>
      </div>
      {isDirty && <p className="mt-2 text-xs text-amber-600 font-medium">Há mudanças não publicadas.</p>}

      {history.length > 0 && (
        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-4" data-testid="home-history">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5 mb-2">
            <History className="w-3.5 h-3.5" /> Versões anteriores
          </p>
          {[...history].reverse().map((h, ri) => {
            const index = history.length - 1 - ri;
            return (
              <div key={index} className="flex items-center justify-between text-sm py-1.5">
                <span className="text-slate-600">Versão de {new Date(h.published_at).toLocaleString("pt-BR")}</span>
                <Button size="sm" variant="outline" onClick={() => restore(index)} data-testid={`home-restore-${index}`}>
                  Restaurar
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
